/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useEffect, useContext, useState } from 'react';
import { Form, FormGroup, Input, Label, Button, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalBody } from 'reactstrap';
import moment from 'moment';
import Alert from 'react-s-alert';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext'

// import services
import ErrorBoundary from '../general/ErrorBoundary';
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

const Audits = props => {

    // variables
    let timeout = 0;

    // states
    const [audits, setAudits] = useState([]);

    const [isLoading, setIsLoading] = useState(false);
    const [modal, setModal] = useState({ isVisible: false, data: {} });
    const [pagination, setPagination] = useState({ limit: 10, page: 1 });
    const [type, setType] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [table, setTable] = useState('');

    // contexts
    const { theme } = useContext(ThemeContext);

    // use Effects
    useEffect(() => {
        handleSearchSubmit(null);
    }, [pagination.page, pagination.limit]);

    const handlePagination = (e, index) => {
        e.preventDefault();
        try {
            setPagination({ ...pagination, page: index });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Audits handlePagination', 'Error in try catch block', err.stack);
        }
    }

    const handleSearchSubmit = e => {
        if (e) e.preventDefault();
        try {
            if (!isLoading) {
                setIsLoading(true);
                AdminService.getAudits(pagination.limit, pagination.page, type, fromDate, toDate, table).then(data => {
                    setAudits(data.audits);
                    if (data.pagination.totalPages <= data.pagination.page) {
                        data.pagination.page = data.pagination.totalPages;
                    }
                    setPagination(data.pagination);
                    setIsLoading(false);
                });
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Audits handleSearchSubmit', 'Error in try catch block', err.stack);
            setIsLoading(false);
        }
    }

    const handleViewData = (table, id) => {
        try {
            AdminService.getAuditData(table, id).then(data => {
                setModal({ isVisible: true, data: data.data });
            }).catch(error => console.log("Some error occured"))
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin Audits handleViewData', 'Error in try catch block', err.stack);
        }
    }

    return (
        <main>
            <div className="container-fluid">
                <h3 className="my-4">Audits</h3>
                <div className="card mb-4">
                    <div className="card-header">
                        Audit List
                    </div>
                    <div className="card-body py-0">
                        <div className="table-responsive">
                            <table className="table table-bordered mb-0" id="dataTable" width="100%" cellSpacing="0">
                                <thead>
                                    <tr>
                                        <td colSpan="6" className="border-0 px-0">
                                            <Form inline onSubmit={handleSearchSubmit}>
                                                <FormGroup className="mr-2">
                                                    <Label className="mr-1" style={theme.formBodyText} >From</Label>
                                                    <Input type="date" id="fromDate" name="fromDate" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="form-control" style={theme.formInput} />
                                                </FormGroup>
                                                <FormGroup className="mr-2">
                                                    <Label className="mr-1" for="toDate" style={theme.formBodyText} >To</Label>
                                                    <Input type="date" id="toDate" name="toDate" value={toDate} onChange={(e) => setToDate(e.target.value)} className="form-control" style={theme.formInput} />
                                                </FormGroup>
                                                <FormGroup className="mr-2">
                                                    <Label className="mr-1" for="type" style={theme.formBodyText}>Type</Label>
                                                    <Input type="select" id="type" name="type" style={theme.formInput} onChange={(e) => setType(e.target.value)} value={type} >
                                                        <option value="">All</option>
                                                        <option value="Add">Add</option>
                                                        <option value="Update">Update</option>
                                                    </Input>
                                                </FormGroup>
                                                <FormGroup className="mr-2">
                                                    <Label className="mr-1" for="table" style={theme.formBodyText} >Table</Label>
                                                    <Input type="select" id="table" name="table" value={table} onChange={(e) => setTable(e.target.value)} className="form-control" style={theme.formInput}>
                                                        <option value="">All</option>
                                                        <option value="Client">Client</option>
                                                        <option value="Provider">Counsellor</option>
                                                        <option value="Session">Session</option>
                                                        <option value="SiteVariables">Site Variables</option>
                                                        <option value="Payment">Payment</option>
                                                    </Input>
                                                </FormGroup>
                                                <Button type="submit" className="my-auto" style={Object.assign({ height: 'fit-content' }, theme.formButton)} disabled={isLoading}>
                                                    {isLoading ? <span className="spinner-border spinner-border-sm mr-1"></span> : null}Search
                                                </Button>
                                            </Form>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th>Date-Time</th>
                                        <th>Type</th>
                                        <th>Document</th>
                                        <th colSpan="3">Detail</th>
                                        {/* <th>OldData</th> */}
                                        {/* <th>NewData</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        audits.map((item, index) => (
                                            <ErrorBoundary errorAt={`At Admin Audits Table${index}`}>
                                                <tr key={index}>
                                                    <td>{moment(item.actionDate).format('DD-MM-YYYY HH:MM A')}</td>
                                                    <td>{item.actionType}</td>
                                                    <td>{item.actionOn}</td>
                                                    <td colSpan="3">{item.actionDetail}</td>
                                                    {/* <td>
                                                {
                                                    item.record.split(',').map((subItem, index) => (
                                                        <a href="#" onClick={() => handleViewData(item.document.split(',')[index],subItem)}>{item.document.split(',')[index]}</a>
                                                    ))
                                                }
                                                </td> */}
                                                    {/* <td>{item.oldValue}</td> */}
                                                    {/* <td>{item.newValue}</td> */}
                                                </tr>
                                            </ErrorBoundary>
                                        ))
                                    }
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan="6" className="border-0 pb-0">
                                            <div className="d-flex">
                                                {/* <div className="d-flex p-2">
                                                    <span>Showing {pagination.totalResults==0 ? 0 : 1 } to</span>
                                                    <Input style={{width:'47px', height: '30px', padding: '14px 5px', margin: '0 5px'}} id="limit" minLength="1" maxLength="4" name="limit" value={pagination.limit>pagination.totalResults ? pagination.totalResults : pagination.limit} onChange={e => handlePagination(e,null)} ></Input>
                                                    <span>of {pagination.totalResults} entries</span>
                                                </div> */}
                                                <ErrorBoundary errorAt="At Admin Audits Pagination">
                                                    <div className="p-2">Showing {pagination.totalResults == 0 ? 0 : (1 + (10 * (pagination.page - 1)))} to {(pagination.limit + (10 * (pagination.page - 1))) > pagination.totalResults ? pagination.totalResults : (pagination.limit + (10 * (pagination.page - 1)))} of {pagination.totalResults} entries</div>
                                                    <Pagination aria-label="Page navigation example" className="ml-auto">
                                                        <PaginationItem disabled={pagination.page == 1}>
                                                            <PaginationLink
                                                                onClick={e => handlePagination(e, pagination.page - 1)}
                                                                previous
                                                                href="#" />
                                                        </PaginationItem>
                                                        {[...Array(pagination.totalPages)].map((page, i) =>
                                                            <PaginationItem active={i + 1 === pagination.page} key={i}>
                                                                <PaginationLink onClick={e => handlePagination(e, i + 1)} href="#">
                                                                    {i + 1}
                                                                </PaginationLink>
                                                            </PaginationItem>
                                                        )}
                                                        <PaginationItem disabled={pagination.page == pagination.totalPages}>
                                                            <PaginationLink
                                                                onClick={e => handlePagination(e, pagination.page + 1)}
                                                                next
                                                                href="#" />
                                                        </PaginationItem>
                                                    </Pagination>
                                                </ErrorBoundary>
                                            </div>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
            <ErrorBoundary errorAt="At Admin Audits Modal">
                <Modal size="lg" isOpen={modal.isVisible} backdrop={true}>
                    <ModalHeader close={<span className="ml-3" style={{ cursor: "pointer" }} onClick={() => setModal({ isVisible: false, data: {} })}><i className="fas fa-times"></i></span>}><span style={theme.headingBold}>Data</span></ModalHeader>
                    <ModalBody>
                        {JSON.stringify(modal.data)}
                    </ModalBody>
                </Modal>
            </ErrorBoundary>
        </main>
    )

}

export default Audits;